import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useContext,
} from "react";
import Aggrid from "../../../framework/AgGrid";
import classes from "./index.module.css";
import moment from "moment";
import CellNumberEditor from "../../../framework/cellEditor/CellNumberEditor";
import GlobalContext from "../../../framework/GlobalContext";
import { selectRow, rowDown, rowUp } from "../../../utils/gridUtils";
import { fetchDiscountProgramDetails } from "../../../services/discount-program";

const DiscountSlabDetails = (props) => {
  const { discountProgramCode = null, onEscape = () => {} } = props;
  const [discountData, setDiscountData] = useState(null);
  const [rowData, setRowData] = useState(null);
  const [api, setApi] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const { currentComponent, setCurrentComponent } = useContext(GlobalContext);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Minimum Amount",
        field: "min",
        width: 200,
      },
      {
        headerName: "Maximum Amount",
        field: "max",
        width: 200,
      },
      {
        headerName: "Discount",
        field: "discPerMedicine",
        width: 200,
      },
    ];
  }, []);

  const getDiscountSlabDetails = useCallback((discountProgramCode) => {
    fetchDiscountProgramDetails(`_id=${discountProgramCode}`).then((resp) => {
      const data = resp?.result?.rows;
      setDiscountData(data);
      const updatedRowData = data?.discountProgramDetails?.map((item) => ({
        ...item,
      }));
      setRowData(updatedRowData);
    });
  }, []);

  const onGridReadyHandler = useCallback(
    (params) => {
      setApi(params.api);
      params.api.sizeColumnsToFit();
      selectRow(params.api, selectedRowIndex);
    },
    [selectedRowIndex]
  );

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        onEscape();
        return;
      }
      if (key === "arrowdown") {
        rowDown(api);
        return;
      }
      if (key === "arrowup") {
        rowUp(api);
        return;
      }
    },
    [api, onEscape]
  );

  useEffect(() => {
    if (discountProgramCode) {
      getDiscountSlabDetails(discountProgramCode);
    }
  }, [getDiscountSlabDetails, discountProgramCode]);

  useEffect(() => {
    setCurrentComponent("grid_discount_slab_details");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (currentComponent === "grid_discount_slab_details") {
      document.addEventListener("keyup", keyupListener, false);
    }
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [api, currentComponent, keyupListener]);

  return (
    <div className={classes["discount-slab-details"]}>
      <div className={classes["discount-slab-details-header"]}>
        <div className={classes["discount-slab-details-header-left"]}>
          <span>
            {discountData?.programName?.toUpperCase()} -{" "}
            {discountData?.discountProgramCode}
          </span>
          <span>
            From -{" "}
            {moment(`${discountData?.startDate}`).format("DD/MM/YYYY hh:mm A")}
          </span>
          <span>
            To -{" "}
            {moment(`${discountData?.endDate}`).format("DD/MM/YYYY hh:mm A")}
          </span>
        </div>
        <div className={classes["discount-slab-details-header-right"]}>
          {discountData?.discountProgramDetails[0]?.categoryName && (
            <span>
              Product Category -{" "}
              {discountData?.discountProgramDetails[0]?.categoryName?.toUpperCase()}{" "}
            </span>
          )}
          {discountData?.discountProgramDetails[0]?.patientCategoryName && (
            <span>
              Customer -{" "}
              {discountData?.discountProgramDetails[0]?.patientCategoryName?.toUpperCase()}{" "}
            </span>
          )}
          {discountData?.discountProgramDetails[0]?.entityName && (
            <span>
              Entity -{" "}
              {discountData?.discountProgramDetails[0]?.entityName?.toUpperCase()}{" "}
            </span>
          )}
        </div>
      </div>

      <div className={classes["discount-slab-details-grid"]}>
        <Aggrid
          id="grid_discount_slab_details"
          className="ag-theme-balham"
          rowData={rowData}
          columnDefs={columnDefs}
          frameworkComponents={{
            cellNumberEditor: CellNumberEditor,
          }}
          defaultColDef={{
            resizable: true,
            cellStyle: (params) => {
              return {
                borderRight: "1px solid #a0a0a0",
              };
            },
            suppressKeyboardEvent: (params) => true,
          }}
          onGridReady={(params) => {
            setTimeout(() => {
              onGridReadyHandler(params);
            }, 100);
          }}
          onCellClicked={(params) => {
            setSelectedRowIndex(params.rowIndex);
          }}
        />
      </div>
    </div>
  );
};

export default DiscountSlabDetails;
